import { onboardingChecklistApi } from './onboarding-checklist.feature';

/**
 * The onboarding setup run: the moment the about-you form is submitted, the
 * API starts Clu's first chat on the server (research, company lookup, sales
 * team count, the company profile), so it is finished or still running by the
 * time the user reaches it. `GET /onboarding/state` is the run's progress.
 */

export type OnboardingSetupStatus = 'none' | 'running' | 'done' | 'failed';

export type OnboardingSetupStepStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'failed';

export interface OnboardingSetupStep {
  id: string;
  /** The Clu tool the step reads as in the steps line (e.g. `read_website`). */
  name: string;
  caption: string;
  status: OnboardingSetupStepStatus;
  started_at: string | null;
  finished_at: string | null;
}

/** Who the onboarding call books with, routed by the company's sales team. */
export interface OnboardingSetupCall {
  host: 'lev' | 'sameer';
  booking_url: string;
  credits: number;
}

export interface OnboardingSetupState {
  conversation_id: string | null;
  status: OnboardingSetupStatus;
  started_at: string | null;
  completed_at: string | null;
  steps: OnboardingSetupStep[];
  call: OnboardingSetupCall | null;
  sales_team: {
    total: number | null;
    sdr_bdr: number | null;
    ae: number | null;
    bdm: number | null;
    sales_manager: number | null;
  } | null;
}

export interface StartOnboardingSetupRequest {
  first_name: string;
  last_name: string;
  job_function: string;
  website: string;
  referral_source: string;
  referral_other?: string | null;
}

export interface StartOnboardingSetupResponse {
  conversation_id: string;
  status: OnboardingSetupStatus;
}

export const onboardingSetupApi = onboardingChecklistApi
  .enhanceEndpoints({ addTagTypes: ['OnboardingSetup'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      startOnboardingSetup: builder.mutation<
        StartOnboardingSetupResponse,
        StartOnboardingSetupRequest
      >({
        query: (body) => ({ url: '/onboarding/start', method: 'POST', body }),
        invalidatesTags: ['OnboardingSetup'],
      }),
      getOnboardingSetupState: builder.query<OnboardingSetupState, void>({
        query: () => '/onboarding/state',
        providesTags: ['OnboardingSetup'],
      }),
    }),
  });

export const {
  useStartOnboardingSetupMutation,
  useGetOnboardingSetupStateQuery,
} = onboardingSetupApi;
